import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Loader2, ChevronLeft, ChevronRight, Pencil, Download } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { formatSydneyDateShort, formatSydneyDateLong } from "../../utils/helpers";
import html2pdf from "html2pdf.js";

interface OfferCandidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface Offer {
  id: number;
  status: "DRAFT" | "SENT" | "ACCEPTED" | "REJECTED" | "EXPIRED";
  salaryOffered: string | null;
  startDate: string | null;
  expiryDate: string | null;
  sentAt: string | null;
  candidate: OfferCandidate;
  requisition: {
    jobRole: { roleName: string } | null;
    department: { departmentName: string } | null;
  } | null;
}

interface Pagination {
  total: number;
  page: number;
  pages: number;
}

interface OfferLetterData {
  id: number;
  status: string;
  salaryOffered: string | number | null;
  startDate: string | null;
  expiryDate: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  offerLetterContent: string | null;
  candidateSignature: string | null;
  candidateSignedDate: string | null;
  candidate: { id: number; firstName: string; lastName: string; email: string } | null;
  requisition: { jobRole: { roleName: string } | null } | null;
  requisitionOrganization: { companyName: string } | null;
}

const formatLetterDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  // sentAt/acceptedAt are ISO timestamps (contain 'T'), startDate/expiryDate are date-only
  const d = dateStr.includes("T") ? new Date(dateStr) : new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return formatSydneyDateLong(d) || "—";
};

const formatLetterCurrency = (amount: string | number | null) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const num = Number(amount);
  if (isNaN(num)) return String(amount);
  return `$${num.toLocaleString()}`;
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case "SENT":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "ACCEPTED":
      return "bg-green-50 text-green-700 border-green-100";
    case "REJECTED":
      return "bg-red-50 text-red-700 border-red-100";
    case "EXPIRED":
      return "bg-gray-50 text-gray-700 border-gray-100";
    case "DRAFT":
      return "bg-orange-50 text-orange-700 border-orange-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "SENT": return "Offer Sent";
    case "ACCEPTED": return "Accepted";
    case "REJECTED": return "Rejected";
    case "EXPIRED": return "Expired";
    case "DRAFT": return "Draft";
    default: return status;
  }
};

const PAGE_SIZE = 10;

const OffersList: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_OFFER) && !ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE)) {
    return <AccessRestricted />;
  }
  const [offers, setOffers] = useState<Offer[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);

  const fetchOffers = useCallback(async (currentPage: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(currentPage));
      params.set("limit", String(PAGE_SIZE));
      if (statusFilter) params.set("status", statusFilter);

      const res = await api.get<{
        success: boolean;
        offers: Offer[];
        pagination: Pagination;
      }>(`/api/offers?${params.toString()}`);

      if (res.success) {
          setOffers(res.offers);
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error("Failed to fetch offers:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOffers(page);
  }, [page, statusFilter, fetchOffers]);

  // Offer letter PDF download
  const [letterOffer, setLetterOffer] = useState<OfferLetterData | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const letterRef = useRef<HTMLDivElement>(null);

  const handleDownloadLetter = async (offerId: number) => {
    setDownloadingId(offerId);
    try {
      const res = await api.get<{ success: boolean; offer: OfferLetterData }>(`/api/offers/${offerId}`);
      if (res.success) {
        setLetterOffer(res.offer);
      } else {
        setDownloadingId(null);
      }
    } catch (error) {
      console.error("Failed to fetch offer letter:", error);
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!letterOffer) return;
    let cancelled = false;

    const generatePdf = async () => {
      const node = letterRef.current;
      if (!node) {
        setLetterOffer(null);
        setDownloadingId(null);
        return;
      }
      // Wait for the signature image to load so it renders in the PDF
      const images = Array.from(node.querySelectorAll("img"));
      await Promise.all(
        images.map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              })
        )
      );
      if (cancelled) return;
      try {
        const opt = {
          margin: 15,
          filename: `Offer-${letterOffer.candidate?.lastName || "Letter"}.pdf`,
          image: { type: "jpeg" as const, quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm" as const, format: "a4" as const, orientation: "portrait" as const },
        };
        await html2pdf().set(opt).from(node).save();
      } catch (error) {
        console.error("Failed to generate offer letter PDF:", error);
      } finally {
        if (!cancelled) {
          setLetterOffer(null);
          setDownloadingId(null);
        }
      }
    };

    generatePdf();
    return () => {
      cancelled = true;
    };
  }, [letterOffer]);

  const filteredOffers = useMemo(() => {
    if (searchQuery === "") return offers;
    const q = searchQuery.toLowerCase();
    return offers.filter((offer) => {
      const candidateName = `${offer.candidate?.firstName || ""} ${offer.candidate?.lastName || ""}`.toLowerCase();
      const email = (offer.candidate?.email || "").toLowerCase();
      const position = (offer.requisition?.jobRole?.roleName || "").toLowerCase();
      const department = (offer.requisition?.department?.departmentName || "").toLowerCase();
      const status = offer.status.toLowerCase();
      return (
        candidateName.includes(q) ||
        email.includes(q) ||
        position.includes(q) ||
        department.includes(q) ||
        status.includes(q)
      );
    });
  }, [searchQuery, offers]);

  const statusOptions = [
    { value: "", label: "All Offers" },
    { value: "DRAFT", label: "Draft" },
    { value: "SENT", label: "Offer Sent" },
    { value: "ACCEPTED", label: "Accepted" },
    { value: "REJECTED", label: "Rejected" },
    { value: "EXPIRED", label: "Expired" },
  ];

  return (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-bold text-gray-900 text-lg">Offer Management</h4>
          <p className="text-gray-500 text-sm font-medium">
            Manage draft and sent offers
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <Input
            placeholder="Search offers..."
            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                statusFilter === opt.value
                  ? "bg-slate-950 text-white"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto no-scrollbar">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                Candidate
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Position
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Department
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Salary
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Start Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                Sent Date
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  <Loader2 size={24} className="animate-spin text-gray-400 mx-auto" />
                </td>
              </tr>
            ) : filteredOffers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-400 text-sm font-medium">
                  {searchQuery || statusFilter ? "No offers match your filters" : "No offers yet"}
                </td>
              </tr>
            ) : (
              filteredOffers.map((offer) => (
                <tr
                  key={offer.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-5">
                    <div>
                      <p className="font-bold text-gray-900 text-sm">
                        {offer.candidate?.firstName} {offer.candidate?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {offer.candidate?.email}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {offer.requisition?.jobRole?.roleName || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {offer.requisition?.department?.departmentName || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <Badge
                      variant="outline"
                      className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusStyle(
                        offer.status
                      )}`}
                    >
                      {formatStatusLabel(offer.status)}
                    </Badge>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {offer.salaryOffered
                        ? `$${Number(offer.salaryOffered).toLocaleString()}`
                        : "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {offer.startDate ? formatSydneyDateShort(offer.startDate) : "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-bold text-gray-600">
                      {offer.sentAt
                        ? formatSydneyDateShort(offer.sentAt)
                        : offer.status === "DRAFT" && offer.startDate
                        ? formatSydneyDateShort(offer.startDate)
                        : "—"}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {offer.status === "DRAFT" ? (
                        <button
                          onClick={() =>
                            navigate(`/recruitment/candidates/${offer.candidate?.id}/offer`)
                          }
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="View / Edit Draft"
                        >
                          <Pencil size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            navigate(`/recruitment/candidates/${offer.candidate?.id}`)
                          }
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="View Candidate"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                      {offer.status === "ACCEPTED" && (
                        <button
                          onClick={() => handleDownloadLetter(offer.id)}
                          disabled={downloadingId === offer.id}
                          className="text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download Offer Letter"
                        >
                          {downloadingId === offer.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm font-medium">
            {searchQuery || statusFilter ? "No offers match your filters" : "No offers yet"}
          </div>
        ) : (
          filteredOffers.map((offer) => (
            <div
              key={offer.id}
              className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm">
                    {offer.candidate?.firstName} {offer.candidate?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    {offer.candidate?.email}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {offer.status === "DRAFT" ? (
                    <button
                      onClick={() => navigate(`/recruitment/candidates/${offer.candidate?.id}/offer`)}
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                      title="View / Edit Draft"
                    >
                      <Pencil size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/recruitment/candidates/${offer.candidate?.id}`)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="View Candidate"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                  {offer.status === "ACCEPTED" && (
                    <button
                      onClick={() => handleDownloadLetter(offer.id)}
                      disabled={downloadingId === offer.id}
                      className="text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download Offer Letter"
                    >
                      {downloadingId === offer.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Position
                  </span>
                  <span className="font-bold text-gray-700">
                    {offer.requisition?.jobRole?.roleName || "—"}
                  </span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Department
                  </span>
                  <span className="font-bold text-gray-700">
                    {offer.requisition?.department?.departmentName || "—"}
                  </span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Status
                  </span>
                  <Badge
                    variant="outline"
                    className={`rounded-lg border-0 font-bold px-2 py-0.5 text-[10px] ${getStatusStyle(
                      offer.status
                    )}`}
                  >
                    {formatStatusLabel(offer.status)}
                  </Badge>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Salary
                  </span>
                  <span className="font-bold text-gray-700">
                    {offer.salaryOffered
                      ? `$${Number(offer.salaryOffered).toLocaleString()}`
                      : "—"}
                  </span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    Start Date
                  </span>
                  <span className="font-bold text-gray-700">
                    {offer.startDate ? formatSydneyDateShort(offer.startDate) : "—"}
                  </span>
                </div>
                <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                    {offer.status === "DRAFT" ? "Created" : "Sent Date"}
                  </span>
                  <span className="font-bold text-gray-700">
                    {offer.sentAt
                      ? formatSydneyDateShort(offer.sentAt)
                      : offer.status === "DRAFT" && offer.startDate
                      ? formatSydneyDateShort(offer.startDate)
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 font-medium">
            Showing {((pagination.page - 1) * PAGE_SIZE) + 1}–{Math.min(pagination.page * PAGE_SIZE, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg border-gray-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-bold text-gray-900 min-w-[3rem] text-center">
              {pagination.page} / {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg border-gray-200"
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages || loading}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Hidden offer letter rendered for PDF generation */}
      {letterOffer && (
        <div
          style={{ position: "fixed", left: "-10000px", top: 0, zIndex: -1 }}
          aria-hidden
        >
          <style>{`
            .offer-letter-pdf p {
              margin: 0;
              padding: 0;
            }
            .offer-letter-pdf p + p {
              margin-top: 0.5em;
            }
            .offer-letter-pdf br {
              display: block;
              content: "";
              margin-top: 0.5em;
            }
            .offer-letter-pdf ul, .offer-letter-pdf ol {
              margin: 0.5em 0;
              padding-left: 1.5em;
            }
            .offer-letter-pdf h1, .offer-letter-pdf h2, .offer-letter-pdf h3,
            .offer-letter-pdf h4, .offer-letter-pdf h5, .offer-letter-pdf h6 {
              margin: 0.5em 0;
              font-weight: 600;
            }
          `}</style>
          <div
            ref={letterRef}
            className="bg-white border border-gray-200 p-12 pb-20"
            style={{ width: "794px" }}
          >
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Offer of Employment</h1>
              <p className="text-slate-500 text-sm mt-1">
                Sent {formatLetterDate(letterOffer.sentAt)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8 bg-slate-50 rounded-lg p-4 border border-slate-100">
              <LetterSummaryRow
                label="Candidate"
                value={
                  letterOffer.candidate
                    ? `${letterOffer.candidate.firstName} ${letterOffer.candidate.lastName}`
                    : "—"
                }
              />
              <LetterSummaryRow
                label="Position"
                value={letterOffer.requisition?.jobRole?.roleName || "—"}
              />
              <LetterSummaryRow
                label="Salary"
                value={formatLetterCurrency(letterOffer.salaryOffered)}
              />
              <LetterSummaryRow label="Start Date" value={formatLetterDate(letterOffer.startDate)} />
              <LetterSummaryRow
                label="Offer Expires"
                value={formatLetterDate(letterOffer.expiryDate)}
              />
              <LetterSummaryRow
                label="Company"
                value={letterOffer.requisitionOrganization?.companyName || "—"}
              />
            </div>

            {letterOffer.offerLetterContent ? (
              <div
                className="text-slate-800 text-[15px] leading-relaxed offer-letter-pdf"
                dangerouslySetInnerHTML={{ __html: letterOffer.offerLetterContent }}
              />
            ) : (
              <p className="text-slate-800 text-[15px] leading-relaxed">
                No offer letter content available.
              </p>
            )}

            {letterOffer.candidateSignature && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Digital Signature
                </p>
                <img
                  src={letterOffer.candidateSignature}
                  alt="Signature"
                  className="h-16 object-contain"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Signed on {formatLetterDate(letterOffer.candidateSignedDate)}
                </p>
              </div>
            )}

            {letterOffer.acceptedAt && (
              <p className="text-xs text-slate-400 mt-4">
                Offer accepted on {formatLetterDate(letterOffer.acceptedAt)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function LetterSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

export default OffersList;